import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Page, Card, Banner, Select, Spinner, Text, BlockStack, InlineStack } from '@shopify/polaris';
import { useAuth } from '../AuthComponent';
import { useStore } from '../StoreContext';
import { useRfidApi } from './useRfidApi';
import { TABS, parseTab, pickWarehouse } from './overstockHelpers';
import BoxesTab from './BoxesTab';
import BoxDetailModal from './BoxDetailModal';
import LocationsTab from './LocationsTab';
import CountsTab from './CountsTab';
import PrintLabelModal from './PrintLabelModal';

const TAB_LABELS = { boxes: 'Boxes', locations: 'Locations', counts: 'Counts' };

export default function Overstock() {
  const { user } = useAuth();
  const { activeStore } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.toString() ? `?${searchParams}` : '');

  const [warehouses, setWarehouses] = useState(null);      // null = loading
  const [warehouse, setWarehouse] = useState(null);
  const [locations, setLocations] = useState([]);
  const [error, setError] = useState('');
  const [toastState, setToastState] = useState(null);       // {kind, msg}
  const [openBoxId, setOpenBoxId] = useState(null);         // page-level detail modal
  const [refreshKey, setRefreshKey] = useState(0);
  const [printOpen, setPrintOpen] = useState(false);

  const storageKey = `rfid_warehouse_${activeStore}`;
  const bootstrapApi = useRfidApi(null);
  const api = useRfidApi(warehouse?.shopify_location_id || null);

  // The app mounts Polaris without a <Frame>, so Polaris <Toast> would throw
  // "No Frame provided". Use the app's Banner-in-a-div toast (.opshub-toast).
  const toastTimer = React.useRef(null);
  const toast = useCallback((kind, msg) => {
    const text = typeof msg === 'string' ? msg : JSON.stringify(msg);
    setToastState({ kind, msg: text, key: Date.now() });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastState(null), 4000);
  }, []);

  // 1. Which warehouses may this user work in?
  useEffect(() => {
    if (!user?.rfid_enabled) return;
    let alive = true;
    bootstrapApi('/api/rfid/config')
      .then(j => {
        if (!alive) return;
        setWarehouses(j.warehouses || []);
        let saved = null;
        try { saved = localStorage.getItem(storageKey); } catch (e) { /* private mode */ }
        setWarehouse(pickWarehouse(j.warehouses || [], saved));
      })
      .catch(e => alive && setError(e.message));
    return () => { alive = false; };
  }, [user?.rfid_enabled, bootstrapApi, storageKey]);

  // 2. Locations for the chosen warehouse (shelf codes + box_count).
  const reloadLocations = useCallback(async () => {
    if (!warehouse) return;
    try {
      const j = await api('/api/rfid/locations?all=1');
      setLocations(j.locations || []);
    } catch (e) { toast('err', e.message); }
  }, [api, warehouse, toast]);

  useEffect(() => { reloadLocations(); }, [reloadLocations]);

  const chooseWarehouse = (sid) => {
    const w = (warehouses || []).find(x => String(x.shopify_location_id) === String(sid)) || null;
    setWarehouse(w);
    try { if (w) localStorage.setItem(storageKey, w.shopify_location_id); } catch (e) { /* ignore */ }
  };

  const setTab = (t) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', t);
    setSearchParams(next, { replace: true });
  };

  const tabProps = useMemo(() => ({
    api, warehouse, locations, reloadLocations, onOpenBox: setOpenBoxId, toast, user, refreshKey,
  }), [api, warehouse, locations, reloadLocations, toast, user, refreshKey]);

  if (!user?.rfid_enabled) {
    return (
      <Page title="Overstock">
        <Banner tone="info" title="Overstock is not enabled on this environment" />
      </Page>
    );
  }
  if (error) {
    return <Page title="Overstock"><Banner tone="critical" title="Could not load Overstock">{error}</Banner></Page>;
  }
  if (warehouses === null) {
    return <Page title="Overstock"><Card><InlineStack align="center"><Spinner size="small" /></InlineStack></Card></Page>;
  }
  if (!warehouses.length) {
    return <Page title="Overstock"><Banner tone="warning" title="No RFID warehouse is assigned to your account" /></Page>;
  }

  const warehouseSelect = warehouses.length > 1 ? (
    <Select
      label="Warehouse"
      labelHidden
      options={warehouses.map(w => ({ label: w.name, value: String(w.shopify_location_id) }))}
      value={warehouse ? String(warehouse.shopify_location_id) : ''}
      placeholder="Choose a warehouse"
      onChange={chooseWarehouse}
    />
  ) : null;

  return (
    <div className="overstock-page">
      <Page
        title="Overstock"
        subtitle={warehouse ? `${warehouse.name} · ${warehouse.overstock_level === 'variant' ? 'variant' : 'product'}-level overstock` : undefined}
        fullWidth
        primaryAction={warehouse ? { content: 'Print label', onAction: () => setPrintOpen(true) } : undefined}
      >
        <BlockStack gap="300">
          <InlineStack gap="300" blockAlign="center">
            {warehouseSelect}
            {warehouse && (
              <div className="overstock-tabs" role="tablist">
                {TABS.map(t => (
                  <button
                    key={t}
                    type="button"
                    role="tab"
                    aria-selected={tab === t}
                    className={`overstock-tab${tab === t ? ' overstock-tab--active' : ''}`}
                    onClick={() => setTab(t)}
                  >
                    {TAB_LABELS[t]}
                  </button>
                ))}
              </div>
            )}
          </InlineStack>

          {!warehouse && <Card><Text as="p" tone="subdued">Choose a warehouse to continue.</Text></Card>}
          {warehouse && tab === 'boxes' && <BoxesTab {...tabProps} />}
          {warehouse && tab === 'locations' && <LocationsTab {...tabProps} />}
          {warehouse && tab === 'counts' && <CountsTab {...tabProps} />}
        </BlockStack>

        {openBoxId && (
          <BoxDetailModal
            api={api}
            boxId={openBoxId}
            onClose={() => setOpenBoxId(null)}
            onChanged={() => { setRefreshKey(k => k + 1); reloadLocations(); }}
            toast={toast}
          />
        )}
        {warehouse && (
          <PrintLabelModal
            open={printOpen}
            onClose={() => setPrintOpen(false)}
            api={api}
            warehouse={warehouse}
            toast={toast}
            onPrinted={() => setRefreshKey(k => k + 1)}
          />
        )}
      </Page>
      {toastState && (
        <div key={toastState.key} className="opshub-toast" role="alert" aria-live="polite">
          <Banner tone={toastState.kind === 'err' ? 'critical' : 'success'} onDismiss={() => setToastState(null)}>
            <Text as="p">{toastState.msg}</Text>
          </Banner>
        </div>
      )}
    </div>
  );
}
